<template>
    <div>
        <div class="row">
            <div class="col-md-12">
                <h3 class="text-dark">
                    <i class="el-icon-bell"></i>
                    <span>Post-Call Actions</span>
                </h3>

                <p>
                    <small>
                        Configure notifications and actions that occur after voice calls end. Set up email notifications 
                        for call summaries and voicemail alerts to keep your team informed and ensure follow-up actions are taken.
                    </small>
                </p>

            </div>

            <div class="col-12">
            <div class="mb-2">
                <div class="form-label">
                    <h5>Email Notification</h5>
                    <small>
                        By enabling this, an email with the summary is sent at the end of each call.
                    </small>
                </div>

                <div class="switch-label d-flex align-items-center">
                    <el-switch
                        v-model="bot.post_call_notification"
                        active-color="#00BF50"
                    />
                    <small class="font-weight-bold custom-toggle-label ml-2">Enable email notification</small>
                </div>

                <el-radio-group
                    v-if="bot.post_call_notification"
                    v-model="bot.post_call_notification_mode"
                    class="w-100 pl-5"
                >
                    <el-radio
                        :label="AloAi.POST_CALL_NOTIFICATION_MODE_CONTACT_OWNER"
                        class="w-100"
                    >
                        <small>Contact owner</small>
                    </el-radio>
                    <el-radio
                        :label="AloAi.POST_CALL_NOTIFICATION_MODE_ALL_USERS"
                        class="w-100"
                    >
                        <small>All users</small>
                    </el-radio>
                    <el-radio
                        :label="AloAi.POST_CALL_NOTIFICATION_MODE_SPECIFIC_USERS"
                        class="w-100"
                    >
                        <small>Specific users</small>
                    </el-radio>
                    <div class="pt-1 pl-4 w-100">
                        <el-form-item
                            :rules="[
                                {
                                    validator: validateSpecificUsers,
                                    trigger: 'change'
                                }
                            ]"
                            prop="post_call_notification_users"
                        >
                            <user-selector
                                v-model="bot.post_call_notification_users"
                                :value="bot.post_call_notification_users"
                                :disabled="bot.post_call_notification_mode !== AloAi.POST_CALL_NOTIFICATION_MODE_SPECIFIC_USERS"
                                :selected-users="selectedUsers"
                                :hide_extensions="true"
                                @change="changeUser"
                                multiple
                                placeholder="Select users to notify"
                            />
                        </el-form-item>
                    </div>
                    <!-- Custom Email Input -->
                    <el-form-item
                        v-if="bot.post_call_notification"
                        class="mt-2 w-100"
                        :rules="[
                            {
                                validator: validateEmailNotification,
                                trigger: ['change', 'blur']
                            }
                        ]"
                        prop="post_call_notification_emails"
                    >
                        <div class="d-flex align-items-center mb-1 custom-label">
                            <small>Send to specific email addresses</small>
                            <el-tooltip
                                :disabled="!bot.post_call_notification_mode"
                                class="ml-2"
                                effect="dark"
                                content="Press 'Enter' to add multiple email addresses."
                                placement="top"
                            >
                                <i class="el-icon-info"/>
                            </el-tooltip>
                        </div>
                        <el-select
                            v-model="bot.post_call_notification_emails"
                            :disabled="!bot.post_call_notification_mode"
                            multiple
                            filterable
                            allow-create
                            default-first-option
                            placeholder="Enter email addresses"
                            class="w-100"
                            @create="handleEmailCreate"
                        >
                            <el-option
                                v-for="(email, index) in emailHistory"
                                :key="'email-option-' + email + '-' + index"
                                :label="email"
                                :value="email"
                            />
                        </el-select>
                    </el-form-item>
                </el-radio-group>
            </div>
        </div>

        <!-- Voicemail Notification Section -->
        <div class="col-12" v-if="hasVoicemailInInstructions">
            <hr class="my-4">
            <div class="mb-2">
                <div class="form-label">
                    <h5>Voicemail Notification</h5>
                    <small>
                        Select users who should receive voicemail notifications when voicemail is left.
                    </small>
                </div>

                <el-form-item
                    :rules="[
                        {
                            validator: validateVoicemailUsers,
                            trigger: 'change'
                        }
                    ]"
                    prop="voicemail_notification_users"
                >
                    <user-selector
                        v-model="bot.voicemail_notification_users"
                        :value="bot.voicemail_notification_users"
                        :selected-users="selectedVoicemailUsers"
                        :hide_extensions="true"
                        @change="changeVoicemailUser"
                        multiple
                        placeholder="Select users to receive voicemail notifications"
                    />
                </el-form-item>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import * as AloAi from '../../../../constants/aloai'

export default {
    name: 'PostCallActionsTab',

    props: {
        bot: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            AloAi,
            loading: false,
            users: [],
            emailHistory: [],
        }
    },

    created() {
        // Initialize emailHistory with any existing emails
        if (this.bot.post_call_notification_emails?.length > 0) {
            this.emailHistory = [...this.bot.post_call_notification_emails]
        }
        
        // Initialize voicemail_notification_users if not already set
        if (!this.bot.voicemail_notification_users) {
            this.$set(this.bot, 'voicemail_notification_users', [])
        }
    },

    watch: {
        'bot.post_call_notification': {
            handler(newValue) {
                if (!newValue) {
                    this.bot.post_call_notification_mode = AloAi.POST_CALL_NOTIFICATION_MODE_DEFAULT
                }
            },
            deep: true
        },
        'bot.post_call_notification_emails': {
            handler(newEmails) {
                // Update emailHistory to include any new emails
                newEmails.forEach(email => {
                    if (!this.emailHistory.includes(email)) {
                        this.emailHistory.push(email)
                    }
                })
            },
            deep: true
        }
    },

    computed: {
        selectedUsers() {
            return this.bot.post_call_notification_users || []
        },
        selectedVoicemailUsers() {
            return this.bot.voicemail_notification_users || []
        },
        hasVoicemailInInstructions() {
            if (!this.bot.instructions || !Array.isArray(this.bot.instructions)) {
                return false
            }

            const instructionsText = this.bot.instructions
                .map(instruction => instruction.content || '')
                .join(' ')
                .toLowerCase()

            return instructionsText.includes('leave voicemail')
        },
    },

    methods: {
        changeUser(users) {
            this.bot.post_call_notification_users = users
        },
        changeVoicemailUser(users) {
            this.bot.voicemail_notification_users = users
        },
        handleAlsoSendToToggle(value) {
            if (!value) {
                // When turning off, set to default mode
                this.bot.post_call_notification_mode = AloAi.POST_CALL_NOTIFICATION_MODE_DEFAULT
            }
        },
        handleEmailCreate(email) {
            // Add new email to history if it doesn't exist
            if (!this.emailHistory.includes(email)) {
                this.emailHistory.push(email)
            }
        },
        validateSpecificUsers(rule, value, callback) {
            if (this.bot.post_call_notification_mode === AloAi.POST_CALL_NOTIFICATION_MODE_SPECIFIC_USERS) {
                if (!value || value?.length === 0) {
                    callback(new Error('Please select at least one user to notify'))
                } else {
                    callback()
                }
            } else {
                callback()
            }
        },
        validateEmailNotification(rule, value, callback) {
            if (value) {
                // If email notification is enabled
                if (this.bot.post_call_notification_mode === AloAi.POST_CALL_NOTIFICATION_MODE_DEFAULT &&
                    (!this.bot.post_call_notification_emails || this.bot.post_call_notification_emails.length === 0)) {
                    callback(new Error('Please either select a notification mode or add at least one email address'))
                } else {
                    callback()
                }
            } else {
                callback()
            }
        },
        validateVoicemailUsers(rule, value, callback) {
            // Voicemail users are optional, so we always validate successfully
            // You can add custom validation logic here if needed
            callback()
        }
    }
}
</script>

<style>
.custom-toggle-label {
    margin-top: 2px;
    color: #6d6e6e;
}

.custom-label {
    color: #686f89;
}
</style>
